import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Platform, Pressable, StyleSheet, TextInput, View } from 'react-native';
import { useRouter } from 'expo-router';
import { useAuthStore, useModalStore, useToastStore } from '@/store';
import { accountApi } from '@/lib/api/account';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import { useModalHelper } from '@/hooks/useModalHelper';
import Text from '@/components/Typography/Text';
import Button from '@/components/Buttons/Button';
import SwitchInput from '@/components/Inputs/Switch';
import { PERSONALIZATION_DEFAULTS, PERSONALIZATION_LIMITS, PERSONALIZATION_OPTIONS, normalizePersonalization } from '@/constants/personalization';

const translationPrefix = 'settings.personalization.profile.';

function MemoryOverview({ navigate, disabled, styles }) {
  const { t } = useTranslation();
  const label = key => t(translationPrefix + key);
  const userId = useAuthStore(state => state.user?.id);
  const preferences = useAuthStore(state => state.user?.preferences?.memory);
  const isPlus = useAuthStore(state => !!state.user?.flags?.isPlus);
  const updateUserPreferences = useAuthStore(state => state.updateUserPreferences);
  const openModal = useModalStore(state => state.actions.openModal);
  const [count, setCount] = useState(null);
  const [countError, setCountError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  const active = useRef(false);
  const savingRef = useRef(false);
  const isCurrent = () => active.current && useAuthStore.getState().user?.id === userId;
  const loadCount = useCallback(async () => {
    setCountError(false);
    try {
      const data = await accountApi.getMemories();
      if (!Array.isArray(data.memories)) throw new Error('Missing memories');
      if (active.current && useAuthStore.getState().user?.id === userId) setCount(data.memories.length);
    } catch (_) {
      if (active.current && useAuthStore.getState().user?.id === userId) setCountError(true);
    }
  }, [userId]);
  useEffect(() => {
    active.current = true;
    loadCount();
    return () => { active.current = false; };
  }, [loadCount]);

  const changeMemory = async (field, value) => {
    if (savingRef.current || disabled) return;
    savingRef.current = true;
    setSaving(true);
    setError(false);
    try {
      const patch = field === 'isEnabled' ? { isEnabled: value } : {
        isAutoGenerationEnabled: value, autoSaveEnabled: value, dreamingEnabled: value,
      };
      await accountApi.updateMemoryPreferences(patch.isEnabled, patch.isAutoGenerationEnabled, patch.autoSaveEnabled, patch.dreamingEnabled);
      if (isCurrent()) updateUserPreferences({ memory: patch });
    } catch (_) {
      if (isCurrent()) setError(true);
    } finally {
      savingRef.current = false;
      if (isCurrent()) setSaving(false);
    }
  };
  const learning = preferences?.isAutoGenerationEnabled !== false && preferences?.autoSaveEnabled !== false && preferences?.dreamingEnabled !== false;
  return (
    <View style={styles.card}>
      <Text size="n" weight="semibold" accessibilityRole="header">{label('memoryTitle')}</Text>
      <Text size="s" color="secondary">{label('memorySubtitle')}</Text>
      <View style={styles.memoryHeader}>
        <View style={{ flex: 1, minWidth: 140 }}>
          <Text size="s" weight="medium">{countError ? label('memoryFailed') : count === null ? label('memoryLoading') : t(translationPrefix + 'memoryCount', { count })}</Text>
        </View>
        {countError && <Button text={label('retry')} variant="secondary" size="small" onPress={loadCount} />}
        <Button text={label('manage')} variant="secondary" size="small" isDisabled={disabled || saving} onPress={() => navigate('/memory')} />
      </View>
      <View style={styles.memoryRow}>
        <View style={styles.rowCopy}>
          <Text size="s" weight="medium">{label('useMemory')}</Text>
          <Text size="xs" color="secondary">{label('useMemoryHint')}</Text>
        </View>
        <SwitchInput value={preferences?.isEnabled !== false} accessibilityLabel={label('useMemory')} disabled={disabled || saving} onValueChange={value => changeMemory('isEnabled', value)} />
      </View>
      <View style={styles.memoryRow}>
        <View style={styles.rowCopy}>
          <Text size="s" weight="medium">{label('learn')}</Text>
          <Text size="xs" color="secondary">{label(isPlus ? 'learnHint' : 'learnPlusHint')}</Text>
        </View>
        {isPlus ? <SwitchInput value={learning} accessibilityLabel={label('learn')} disabled={disabled || saving} onValueChange={value => changeMemory('learning', value)} /> :
          <Button text={label('plus')} variant="secondary" size="small" isDisabled={disabled} onPress={() => Platform.OS === 'web' ? openModal('PricingModal') : navigate('/(auth)/paywall')} />}
      </View>
      {saving && <ActivityIndicator size="small" />}
      {error && <Text size="s" color="danger" accessibilityRole="alert">{label('memorySaveFailed')}</Text>}
    </View>
  );
}

export default function PersonalizationProfileSettings({ onDirtyChange }) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const label = key => t(translationPrefix + key);
  const userId = useAuthStore(state => state.user?.id);
  const updateUserPreferences = useAuthStore(state => state.updateUserPreferences);
  const addToast = useToastStore(state => state.actions.addToast);
  const router = useRouter();
  const { useConfirmationModal } = useModalHelper();
  const confirm = useConfirmationModal();
  const [saved, setSaved] = useState(PERSONALIZATION_DEFAULTS);
  const [draft, setDraft] = useState(PERSONALIZATION_DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [saving, setSaving] = useState(false);
  const active = useRef(false);
  const savingRef = useRef(false);
  const loadVersion = useRef(0);
  const dirty = JSON.stringify(draft) !== JSON.stringify(saved);
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const isCurrent = () => active.current && useAuthStore.getState().user?.id === userId;

  const load = useCallback(async () => {
    const version = ++loadVersion.current;
    setLoading(true);
    setLoadError(false);
    setUnavailable(false);
    try {
      const result = normalizePersonalization(await accountApi.getPersonalization());
      if (active.current && version === loadVersion.current && useAuthStore.getState().user?.id === userId) {
        setSaved(result);
        setDraft(result);
        updateUserPreferences({ personalization: result });
      }
    } catch (error) {
      if (active.current && version === loadVersion.current && useAuthStore.getState().user?.id === userId) {
        // Mac binaries can reach users before this optional backend route.
        // Keep the existing model controls and account preferences intact;
        // reopening settings probes again after the backend is deployed.
        if (Platform.OS === 'macos' && error?.status === 404) setUnavailable(true);
        else setLoadError(true);
      }
    } finally {
      if (active.current && version === loadVersion.current && useAuthStore.getState().user?.id === userId) setLoading(false);
    }
  }, [userId, updateUserPreferences]);
  useEffect(() => {
    active.current = true;
    load();
    return () => { active.current = false; loadVersion.current += 1; };
  }, [load]);
  useEffect(() => { onDirtyChange?.(dirty || saving); }, [dirty, saving, onDirtyChange]);
  const edit = (key, value) => {
    setDraft(previous => ({ ...previous, [key]: value }));
    setSaveError(false);
  };
  const save = async () => {
    if (!dirty || savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setSaveError(false);
    try {
      const result = normalizePersonalization(await accountApi.updatePersonalization(draft));
      if (isCurrent()) {
        setSaved(result);
        setDraft(result);
        updateUserPreferences({ personalization: result });
        addToast(label('saved'), 'success');
      }
    } catch (_) {
      if (isCurrent()) setSaveError(true);
    } finally {
      savingRef.current = false;
      if (isCurrent()) setSaving(false);
    }
  };
  const navigate = path => {
    if (saving) return;
    if (!dirty) return router.push(path);
    confirm({
      title: t('settingsPage.unsavedTitle'), message: t('settingsPage.unsavedMessage'), confirmText: t('settingsPage.discard'),
      onConfirm: () => { setDraft(saved); setSaveError(false); onDirtyChange?.(false); router.push(path); },
    });
  };
  const field = (key, multiline = false) => (
    <View key={key} style={[styles.field, key === 'customInstructions' && { flexGrow: 0, flexBasis: 'auto' }]}>
      <Text size="s" weight="medium">{label('fields.' + key)}</Text>
      <TextInput
        accessibilityLabel={label('fields.' + key)}
        placeholder={label('placeholders.' + key)} placeholderTextColor={colors.text_color_tertiary}
        value={draft[key]} onChangeText={value => edit(key, value)} editable={!saving}
        maxLength={PERSONALIZATION_LIMITS[key]} multiline={multiline}
        style={[styles.input, multiline && styles.multiline, key === 'customInstructions' && { minHeight: 132 }]}
      />
      {multiline && <Text size="xxs" color="tertiary" style={styles.count}>{draft[key].length} / {PERSONALIZATION_LIMITS[key]}</Text>}
    </View>
  );

  if (loading) return <View style={styles.loading}><ActivityIndicator /><Text size="s" color="secondary">{label('loading')}</Text></View>;
  if (unavailable) return null;
  if (loadError) return <View style={styles.card}><Text color="danger" accessibilityRole="alert">{label('loadFailed')}</Text><Button text={label('retry')} variant="secondary" onPress={load} /></View>;
  return (
    <View style={styles.container}>
      <View style={styles.card}>
        <Text size="n" weight="semibold" accessibilityRole="header">{label('aboutTitle')}</Text>
        <Text size="s" color="secondary">{label('aboutSubtitle')}</Text>
        <View style={styles.fields}>{field('preferredName')}{field('occupation')}</View>
        <View style={styles.fields}>{field('interests', true)}{field('goals', true)}</View>
      </View>
      <View style={styles.card}>
        <Text size="n" weight="semibold" accessibilityRole="header">{label('styleTitle')}</Text>
        <Text size="s" color="secondary">{label('styleSubtitle')}</Text>
        {Object.entries(PERSONALIZATION_OPTIONS).map(([key, options]) => (
          <View key={key} style={styles.styleGroup}>
            <Text size="s" weight="medium">{label('fields.' + key)}</Text>
            <View accessibilityRole="radiogroup" accessibilityLabel={label('fields.' + key)} style={styles.choices}>
              {options.map(option => <Pressable key={option} accessibilityRole="radio" accessibilityState={{ checked: draft[key] === option, disabled: saving }}
                {...(Platform.OS === 'web' ? { 'aria-checked': draft[key] === option } : {})}
                accessibilityLabel={`${label('fields.' + key)}: ${label('options.' + option)}`} disabled={saving} onPress={() => edit(key, option)}
                style={({ hovered, pressed }) => [styles.choice, (hovered || pressed) && styles.hover, draft[key] === option && styles.selected]}>
                <Text size="s" weight={draft[key] === option ? 'semibold' : 'regular'}>{label('options.' + option)}</Text>
              </Pressable>)}
            </View>
          </View>
        ))}
      </View>
      <View style={styles.card}>
        <Text size="n" weight="semibold" accessibilityRole="header">{label('instructionsTitle')}</Text>
        <Text size="s" color="secondary">{label('instructionsSubtitle')}</Text>
        {field('customInstructions', true)}
      </View>
      <Text size="xs" color="secondary">{label('scope')}</Text>
      {saveError && <Text size="s" color="danger" accessibilityRole="alert">{label('saveFailed')}</Text>}
      <View style={styles.actions}>
        <Button text={label('save')} variant="primary" size="small" isLoading={saving} isDisabled={!dirty || saving} onPress={save} />
        {dirty && <Button text={label('discard')} variant="secondary" size="small" isDisabled={saving} onPress={() => { setDraft(saved); setSaveError(false); }} />}
        {dirty && <Text size="xs" color="secondary">{label('unsaved')}</Text>}
      </View>
      <MemoryOverview styles={styles} navigate={navigate} disabled={saving} />
    </View>
  );
}

const makeStyles = colors => StyleSheet.create({
  container: { gap: 18, marginBottom: 12 },
  card: { borderWidth: 1, borderColor: colors.border_color_secondary, backgroundColor: colors.solid_color_block, borderRadius: 14, padding: 20, gap: 12 },
  loading: { padding: 24, flexDirection: 'row', gap: 12, alignItems: 'center' },
  fields: { flexDirection: 'row', flexWrap: 'wrap', gap: 16 },
  field: { flexGrow: 1, flexShrink: 1, flexBasis: 240, minWidth: 0, gap: 8 },
  input: { borderWidth: 1, borderColor: colors.border_color_secondary, borderRadius: 8, padding: 12, fontSize: 15, color: colors.text_color_primary, backgroundColor: colors.solid_color_primary, minHeight: 44 },
  multiline: { minHeight: 90, textAlignVertical: 'top' },
  count: { textAlign: 'right' },
  styleGroup: { gap: 10, marginTop: 4 },
  choices: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  choice: { paddingHorizontal: 16, paddingVertical: 11, borderWidth: 1, borderColor: colors.border_color_secondary, borderRadius: 8, minHeight: 44, justifyContent: 'center' },
  selected: { borderColor: colors.text_color_primary, backgroundColor: colors.solid_color_tertiary },
  hover: { backgroundColor: colors.solid_color_tertiary },
  actions: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 12, paddingBottom: 6 },
  memoryHeader: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 12, paddingVertical: 8 },
  memoryRow: { flexDirection: 'row', alignItems: 'center', gap: 20, borderTopWidth: StyleSheet.hairlineWidth, borderColor: colors.border_color_secondary, paddingTop: 16, paddingBottom: 4 },
  rowCopy: { flex: 1, gap: 5 },
});
